import React, { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { GreenSwitch } from "./ui/green-toggle";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import {
  Search,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Calendar,
  Star,
  Eye,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { feedbacksApi } from "../utils/api";
import { toast } from "sonner";
import Image from "./Image";

function formatDateTime(input) {
  if (!input) return "—";
  try {
    const date = new Date(input);
    if (isNaN(date.getTime())) return String(input);
    return new Intl.DateTimeFormat("en-GB", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    })
      .format(date)
      .replace(/, (\d{2}:\d{2})/, ", $1");
  } catch {
    return String(input);
  }
}

function getFeedbackId(row) {
  return row._id ?? row.id ?? "";
}

function normalizeFeedback(row) {
  const id = getFeedbackId(row);
  return {
    id,
    name: row.name ?? row.userName ?? "—",
    contact: row.contact ?? row.email ?? row.phone ?? "—",
    userId:
      row.userId ??
      row.user_id ??
      row.user?.id ??
      row.user?._id ??
      "—",
    rating: row.rating,
    comment: row.comment ?? row.message ?? "",
    issueType: row.issueType ?? row.issue_type ?? row.type ?? "—",
    createdAt: row.createdAt ?? row.created_at,
    isVisible: row.isVisible ?? row.is_visible ?? false,
    image: row.image,
    raw: row,
  };
}

export function Feedbacks() {
  const [searchTerm, setSearchTerm] = useState("");
  const [visibilityFilter, setVisibilityFilter] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [feedbacks, setFeedbacks] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [visibleById, setVisibleById] = useState({});
  const [toggleLoadingIds, setToggleLoadingIds] = useState(new Set());
  const [viewRow, setViewRow] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);

  const { startDateParam, endDateParam } = useMemo(() => {
    if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
      return { startDateParam: undefined, endDateParam: undefined };
    }
    const toISOStartOfDay = (d) => {
      const date = new Date(d);
      date.setHours(0, 0, 0, 0);
      return date.toISOString();
    };
    const toISOEndOfDay = (d) => {
      const date = new Date(d);
      date.setHours(23, 59, 59, 999);
      return date.toISOString();
    };
    return {
      startDateParam: startDate ? toISOStartOfDay(startDate) : undefined,
      endDateParam: endDate ? toISOEndOfDay(endDate) : undefined,
    };
  }, [startDate, endDate]);

  const isVisibleQuery =
    visibilityFilter === "visible"
      ? true
      : visibilityFilter === "hidden"
        ? false
        : undefined;

  const fetchFeedbacks = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setFeedbacks([]);
        setTotalPages(0);
        setTotal(0);
        return;
      }

      const response = await feedbacksApi.getFeedbacks(
        currentPage,
        limit,
        searchTerm,
        {
          isVisible: isVisibleQuery,
          startDate: startDateParam,
          endDate: endDateParam,
        }
      );

      const payload = response?.data.docs ?? response?.data ?? response;
      const list =
        payload?.feedbacks ??
        payload?.items ??
        payload?.results ??
        (Array.isArray(payload) ? payload : []);

      const pagination =
        payload?.pagination ??
        response?.pagination ??
        (response?.data && response.data.pagination);
        console.log("Paylaod: ", list);

      const rows = Array.isArray(list) ? list.map(normalizeFeedback) : [];
      setFeedbacks(rows);

      if (pagination) {
        setTotalPages(pagination.totalPages ?? pagination.total_pages ?? 0);
        setTotal(pagination.total ?? pagination.count ?? rows.length);
      } else {
        setTotalPages(rows.length ? 1 : 0);
        setTotal(rows.length);
      }

      const vis = {};
      rows.forEach((r) => {
        if (r.id) vis[r.id] = !!r.isVisible;
      });
      setVisibleById(vis);
    } catch (error) {
      console.error("Error fetching feedbacks:", error);
      toast.error(
        error?.message || "Failed to load feedbacks. Please try again."
      );
      setFeedbacks([]);
      setTotalPages(0);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFeedbacks();
  }, [
    currentPage,
    limit,
    searchTerm,
    visibilityFilter,
    startDateParam,
    endDateParam,
  ]);

  const handleSearch = (value) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleVisibilityFilter = (value) => {
    setVisibilityFilter(value);
    setCurrentPage(1);
  };

  const handleStartDate = (value) => {
    setStartDate(value);
    setCurrentPage(1);
  };

  const handleEndDate = (value) => {
    setEndDate(value);
    setCurrentPage(1);
  };

  const handleLimitChange = (value) => {
    setLimit(parseInt(value, 10));
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleVisibilityToggle = async (id) => {
    if (!id) return;
    const previous = !!visibleById[id];
    const next = !previous;

    setToggleLoadingIds((prev) => new Set(prev).add(id));
    setVisibleById((prev) => ({ ...prev, [id]: next }));

    try {
      await feedbacksApi.patchVisibility(id, next);
      toast.success(
        next ? "Feedback is now visible on the website." : "Feedback hidden from the website."
      );
    } catch (error) {
      setVisibleById((prev) => ({ ...prev, [id]: previous }));
      toast.error(
        error?.message || "Could not update visibility. Please try again."
      );
    } finally {
      setToggleLoadingIds((prev) => {
        const nextSet = new Set(prev);
        nextSet.delete(id);
        return nextSet;
      });
    }
  };

  const renderPagination = () => {
    const pages = [];
    const maxVisiblePages = 5;
    const safeTotalPages = Math.max(totalPages, 1);
    let startPage = Math.max(
      1,
      currentPage - Math.floor(maxVisiblePages / 2)
    );
    let endPage = Math.min(totalPages || 1, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    if (startPage > 1) {
      pages.push(
        <Button
          key="first"
          variant="outline"
          size="sm"
          onClick={() => handlePageChange(1)}
          disabled={currentPage === 1 || totalPages <= 1}
        >
          <ChevronsLeft className="w-4 h-4" />
        </Button>
      );
    }

    pages.push(
      <Button
        key="prev"
        variant="outline"
        size="sm"
        onClick={() => handlePageChange(currentPage - 1)}
        disabled={currentPage === 1 || totalPages <= 1}
      >
        <ChevronLeft className="w-4 h-4" />
      </Button>
    );

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <Button
          key={i}
          variant={currentPage === i ? "default" : "outline"}
          size="sm"
          onClick={() => handlePageChange(i)}
        >
          {i}
        </Button>
      );
    }

    pages.push(
      <Button
        key="next"
        variant="outline"
        size="sm"
        onClick={() => handlePageChange(currentPage + 1)}
        disabled={currentPage >= totalPages || totalPages <= 1}
      >
        <ChevronRight className="w-4 h-4" />
      </Button>
    );

    if (endPage < totalPages) {
      pages.push(
        <Button
          key="last"
          variant="outline"
          size="sm"
          onClick={() => handlePageChange(totalPages)}
          disabled={currentPage === totalPages || totalPages <= 1}
        >
          <ChevronsRight className="w-4 h-4" />
        </Button>
      );
    }

    return (
      <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-6">
        <p className="text-sm text-muted-foreground order-2 sm:order-1">
          Page {currentPage} of {safeTotalPages}
          {total > 0 && (
            <span className="hidden sm:inline"> · {total} total</span>
          )}
        </p>
        <div className="flex flex-wrap justify-center gap-2 order-1 sm:order-2">
          {pages}
        </div>
      </div>
    );
  };

  const renderRating = (rating) => {
    if (rating === undefined || rating === null || rating === "") {
      return <span className="text-muted-foreground">—</span>;
    }
    const n = Number(rating);
    if (isNaN(n)) {
      return <span className="text-sm">{String(rating)}</span>;
    }
    return (
      <span className="inline-flex items-center gap-1 text-amber-500 font-medium">
        <Star className="w-4 h-4 fill-amber-400 text-amber-500" aria-hidden />
        {n}
      </span>
    );
  };

  return (
    <div className="space-y-4 lg:space-y-6">
      <div>
        <h1 className="text-2xl lg:text-3xl font-bold mb-2">Feedback</h1>
        <p className="text-sm lg:text-base text-muted-foreground">
          Review submissions, search, filter by visibility and date, and control
          what appears on the website.
        </p>
      </div>

      <Card className="shadow-sm">
        <CardContent className="pt-4 lg:pt-6">
          <div className="flex flex-col gap-4">
            <div className="relative flex-1 w-full">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, contact, or comment..."
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                className="pl-10 text-sm lg:text-base"
              />
            </div>
            <div className="overflow-x-auto -mx-1 px-1">
              <div className="flex flex-nowrap gap-4 items-end min-w-max pb-1">
                <div className="w-[220px]">
                <label className="block text-sm mb-2 text-foreground">
                  Visibility
                </label>
                <Select
                  value={visibilityFilter}
                  onValueChange={handleVisibilityFilter}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Visibility" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="visible">Visible</SelectItem>
                    <SelectItem value="hidden">Hidden</SelectItem>
                  </SelectContent>
                </Select>
                </div>
                <div className="w-[220px]">
                <label className="block text-sm mb-2 text-foreground">
                  Start date
                </label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    type="date"
                    value={startDate}
                    onChange={(e) => handleStartDate(e.target.value)}
                    className="pl-10 text-sm lg:text-base"
                  />
                </div>
                </div>
                <div className="w-[220px]">
                <label className="block text-sm mb-2 text-foreground">
                  End date
                </label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    type="date"
                    value={endDate}
                    onChange={(e) => handleEndDate(e.target.value)}
                    className="pl-10 text-sm lg:text-base"
                  />
                </div>
                </div>
                <div className="w-[220px]">
                <label className="block text-sm mb-2 text-foreground">
                  Per page
                </label>
                <Select value={limit.toString()} onValueChange={handleLimitChange}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Items per page" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="25">25</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg lg:text-xl">
            All feedback ({total} total)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="text-muted-foreground">Loading feedbacks…</div>
            </div>
          ) : feedbacks.length === 0 ? (
            <div className="flex justify-center items-center py-12">
              <p className="text-muted-foreground">No feedbacks found</p>
            </div>
          ) : (
            <>
              <div className="hidden xl:block overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Contact</TableHead>
                      <TableHead>User Id</TableHead>
                      <TableHead>Rating</TableHead>
                      <TableHead>Image</TableHead>
                      <TableHead className="max-w-[220px]">Comment</TableHead>
                      <TableHead>Issue type</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead>Visible</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {feedbacks.map((row, idx) => (
                      <TableRow
                        key={row.id || `feedback-${idx}`}
                        className="hover:bg-muted/50"
                      >
                        <TableCell className="font-medium text-sm">
                          {row.name}
                        </TableCell>
                        <TableCell className="text-sm">{row.contact}</TableCell>
                        <TableCell className="font-mono text-xs">
                          {row.userId}
                        </TableCell>
                        <TableCell>{renderRating(row.rating)}</TableCell>
                        <TableCell>
                          {row.image ? (
                            <div
                              className="relative w-10 h-10 rounded-md overflow-hidden border border-border shadow-sm cursor-zoom-in hover:scale-105 transition-transform duration-200"
                              onClick={() => setPreviewImage(row.image)}
                            >
                              <Image
                                fileId={row.image}
                                alt="Feedback thumbnail"
                                className="w-full h-full object-cover"
                              />
                            </div>
                          ) : (
                            <span className="text-muted-foreground text-sm">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-sm max-w-[220px] truncate">
                          {row.comment || "—"}
                        </TableCell>
                        <TableCell className="text-sm">{row.issueType}</TableCell>
                        <TableCell className="font-mono text-sm">
                          {formatDateTime(row.createdAt)}
                        </TableCell>
                        <TableCell>
                          <GreenSwitch
                            checked={!!visibleById[row.id]}
                            onChange={() => handleVisibilityToggle(row.id)}
                            aria-label="Toggle website visibility"
                            disabled={toggleLoadingIds.has(row.id)}
                          />
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="outline"
                            size="sm"
                            className="gap-1"
                            onClick={() => setViewRow(row)}
                          >
                            <Eye className="w-4 h-4" />
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <div className="xl:hidden space-y-3">
                {feedbacks.map((row, idx) => (
                  <div
                    key={row.id || `feedback-${idx}`}
                    className="border rounded-lg p-4 space-y-3 shadow-sm hover:bg-muted/50 transition-colors"
                    style={{ borderColor: "var(--border)" }}
                  >
                    <div className="flex justify-between items-start gap-2">
                      <div className="flex gap-3 items-center">
                        {row.image && (
                          <div
                            className="relative w-12 h-12 rounded-lg overflow-hidden border border-border shadow-sm flex-shrink-0 cursor-zoom-in hover:scale-105 transition-transform duration-200"
                            onClick={() => setPreviewImage(row.image)}
                          >
                            <Image
                              fileId={row.image}
                              alt="Feedback thumbnail"
                              className="w-full h-full object-cover"
                            />
                          </div>
                        )}
                        <div>
                          <p className="font-medium">{row.name}</p>
                          <p className="text-sm text-muted-foreground">
                            {row.contact}
                          </p>
                        </div>
                      </div>
                      {renderRating(row.rating)}
                    </div>
                    <p className="text-xs font-mono text-muted-foreground">
                      User: {row.userId}
                    </p>
                    <p className="text-sm">
                      <span className="text-muted-foreground">Comment: </span>
                      {row.comment || "—"}
                    </p>
                    <div className="flex flex-wrap gap-2 text-sm">
                      <span className="text-muted-foreground">Issue:</span>
                      <span>{row.issueType}</span>
                    </div>
                    <p className="text-xs font-mono text-muted-foreground">
                      {formatDateTime(row.createdAt)}
                    </p>
                    <div className="flex items-center justify-between pt-2 border-t border-border">
                      <span className="text-sm text-muted-foreground">
                        Visible on site
                      </span>
                      <GreenSwitch
                        checked={!!visibleById[row.id]}
                        onChange={() => handleVisibilityToggle(row.id)}
                        aria-label="Toggle website visibility"
                        disabled={toggleLoadingIds.has(row.id)}
                      />
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full gap-1"
                      onClick={() => setViewRow(row)}
                    >
                      <Eye className="w-4 h-4" />
                      View details
                    </Button>
                  </div>
                ))}
              </div>

              {totalPages > 1 && renderPagination()}
              {totalPages <= 1 && total > 0 && (
                <div className="mt-6 text-center text-sm text-muted-foreground">
                  Page {currentPage} of {Math.max(totalPages, 1)}
                  {total > 0 && ` · ${total} total`}
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!viewRow} onOpenChange={(open) => !open && setViewRow(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Feedback details</DialogTitle>
          </DialogHeader>
          {viewRow && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-muted-foreground">Name</p>
                  <p className="font-medium">{viewRow.name}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Contact</p>
                  <p>{viewRow.contact}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-muted-foreground">User Id</p>
                  <p className="font-mono text-xs break-all">{viewRow.userId}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Rating</p>
                  <div>{renderRating(viewRow.rating)}</div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-muted-foreground">Issue type</p>
                  <p>{viewRow.issueType}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Created</p>
                  <p className="font-mono text-xs">
                    {formatDateTime(viewRow.createdAt)}
                  </p>
                </div>
              </div>
              <div>
                <p className="text-muted-foreground">Comment</p>
                <p className="whitespace-pre-wrap">
                  {viewRow.comment || "—"}
                </p>
              </div>
              <div className="pt-2 border-t border-border">
                <p className="text-muted-foreground mb-2">Attached Image</p>
                {viewRow.image ? (
                  <div
                    className="relative group overflow-hidden rounded-lg border max-h-60 flex justify-center bg-muted/40 cursor-zoom-in shadow-sm hover:border-primary/50 transition-all duration-200"
                    onClick={() => setPreviewImage(viewRow.image)}
                  >
                    <Image
                      fileId={viewRow.image}
                      alt="Feedback attachment"
                      className="max-h-60 w-auto object-contain transition-transform duration-200 group-hover:scale-[1.02]"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 flex items-center justify-center transition-colors duration-200">
                      <span className="text-xs bg-black/60 text-white px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                        Click to enlarge
                      </span>
                    </div>
                  </div>
                ) : (
                  <p className="text-muted-foreground italic">No image attached</p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Fullscreen Image Preview Lightbox */}
      <Dialog open={!!previewImage} onOpenChange={(open) => !open && setPreviewImage(null)}>
        <DialogContent className="max-w-3xl border-none bg-black/95 p-0 overflow-hidden rounded-2xl shadow-2xl flex flex-col items-center justify-center">
          <DialogHeader className="sr-only">
            <DialogTitle>Image Preview</DialogTitle>
          </DialogHeader>
          <div className="relative w-full h-[70vh] flex items-center justify-center p-4">
            {previewImage && (
              <Image
                fileId={previewImage}
                alt="Feedback attachment full view"
                className="max-w-full max-h-full object-contain rounded-lg shadow-md"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
